<template>
  <div class="w-full">
    <h1 class="mb-8 text-3xl font-bold">
      Specjalizacje
    </h1>
    <div class="mb-6 flex flex-wrap items-end gap-x-4 gap-y-2">
      <div>
        <label
          for="specialisation-filter-name"
          class="mb-1 block text-xs font-medium text-gray-600"
        >
          Nazwa
        </label>
        <input
          id="specialisation-filter-name"
          v-model="form.name"
          type="search"
          autocomplete="off"
          placeholder="Szukaj…"
          class="form-input w-[200px] py-1.5 text-sm"
        >
      </div>
      <div>
        <label
          for="specialisation-filter-site"
          class="mb-1 block text-xs font-medium text-gray-600"
        >
          Witryna
        </label>
        <site-context-select
          id="specialisation-filter-site"
          v-model="form.site"
          :options="site_context_options"
          allow-blank
          blank-label="Wszystkie"
          class="w-[240px]"
          compact
        />
      </div>
      <button
        v-if="hasActiveFilters"
        type="button"
        class="text-sm text-gray-500 hover:text-gray-800"
        @click="resetFilters"
      >
        Wyczyść
      </button>
      <button class="btn-indigo ml-auto" @click="modalNew = true">
        Dodaj specjalizację
      </button>
      <modal
        :open="modalNew"
        title="Dodaj specjalizację"
        @close="modalNew = false"
      >
        <new-specialisation
          :site_context_options="site_context_options"
          @success="modalNew = false"
        />
      </modal>
    </div>
    <div class="overflow-x-auto rounded-sm bg-white shadow-sm">
      <table
        class="w-full whitespace-nowrap"
        :class="{ 'is-dnd-dragging': dragging }"
      >
        <thead>
          <tr class="text-left font-bold">
            <th class="w-px px-4 pb-4 pt-6">
              <!-- Drag handle column -->
            </th>
            <th class="px-6 pb-4 pt-6 text-green-700">
              Nazwa
            </th>
            <th class="px-6 pb-4 pt-6">
              Witryna
            </th>
            <th class="px-6 pb-4 pt-6">
              Opis
            </th>
            <th class="px-6 pb-4 pt-6 text-green-700">
              Dane aktualizacji
            </th>
            <th class="px-6 pb-4 pt-6 text-center">
              Kontent
            </th>
            <th class="px-6 pb-4 pt-6">
              Aktywna
            </th>
            <th class="px-6 pb-4 pt-6">
              <!-- Actions -->
            </th>
          </tr>
        </thead>
        <draggable
          v-if="localSpecialisations.length"
          v-model="localSpecialisations"
          tag="tbody"
          item-key="id"
          handle=".drag-handle"
          ghost-class="dnd-drop-ghost"
          chosen-class="dnd-chosen"
          @start="onDragStart"
          @end="onDragEnd"
        >
          <template #item="{ element: specialisation }">
            <tr
              :key="specialisation.id"
              class="focus-within:bg-gray-100 hover:bg-gray-100"
            >
              <td class="w-px border-t px-4">
                <div class="drag-handle cursor-grab active:cursor-grabbing" title="Drag to reorder">
                  <svg class="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M7 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 2zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 14zm6-8a2 2 0 1 0-.001-4.001A2 2 0 0 0 13 6zm0 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 14z" />
                  </svg>
                </div>
              </td>
              <td class="border-t">
                <div class="flex items-center px-6 py-4">
                  {{ specialisation.name }}
                </div>
              </td>
              <td class="border-t">
                <div class="flex items-center px-6 py-4">
                  <site-context-select
                    :model-value="specialisation.site_context"
                    :options="site_context_options"
                    :disabled="updatingSiteId === specialisation.id"
                    allow-blank
                    highlight-when-blank
                    blank-label="Wybierz witrynę"
                    class="max-w-[240px]"
                    compact
                    @update:model-value="updateSiteContext(specialisation, $event)"
                  />
                </div>
              </td>
              <td class="border-t">
                <div class="flex items-center px-6 py-4">
                  <span
                    v-if="specialisation.description"
                    class="max-w-md truncate"
                  >{{ specialisation.description }}</span>
                  <span
                    v-else-if="specialisation.missing_description"
                    class="text-sm text-amber-700"
                  >
                    Brak opisu
                  </span>
                  <span v-else class="text-gray-400">—</span>
                </div>
              </td>
              <td class="border-t">
                <record-update-dates
                  :synced-at="specialisation.synced_at"
                  :updated-at="specialisation.updated_at"
                />
              </td>
              <td class="border-t">
                <div class="flex items-center justify-center px-6 py-4">
                  <content-missing-dot :items="specialisation.missing_content || []" />
                </div>
              </td>
              <td class="border-t">
                <div class="flex items-center px-6 py-4">
                  <toggle-switch
                    :model-value="specialisation.active"
                    :disabled="togglingId === specialisation.id"
                    @update:model-value="toggleActive(specialisation)"
                  />
                </div>
              </td>
              <td class="w-px border-t">
                <div class="ml-4 flex shrink-0 items-center gap-2 px-4">
                  <a
                    v-if="specialisation.id"
                    class="btn-action-view"
                    :href="frontofficeHref(specialisation)"
                    target="_blank"
                    title="Zobacz na stronie"
                  >
                    <icon name="eye" class="mr-1.5 h-3.5 w-3.5" />
                    Podgląd na stronie
                  </a>
                  <Link
                    v-if="specialisation.id"
                    class="btn-action-edit"
                    :href="editRoute(specialisation.id)"
                    title="Edytuj"
                  >
                    <icon name="edit" class="mr-1.5 h-3.5 w-3.5 stroke-current" />
                    Edytuj
                  </Link>
                  <button
                    v-if="specialisation.id"
                    type="button"
                    class="btn-action-delete"
                    title="Usuń"
                    @click.stop="destroy(specialisation)"
                  >
                    <icon name="trash" class="mr-1.5 h-3.5 w-3.5 stroke-current" />
                    Usuń
                  </button>
                </div>
              </td>
            </tr>
          </template>
        </draggable>
        <tbody v-else>
          <tr>
            <td class="border-t px-6 py-4" colspan="8">
              {{ emptyMessage }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <pagination :meta="specialisations.meta" />
  </div>
</template>

<script>
import { Link } from '@inertiajs/vue3';
import Icon from '@/Shared/Icon.vue';
import Pagination from '@/Shared/Pagination.vue';
import pickBy from 'lodash/pickBy';
import Modal from '@/Shared/Modal.vue';
import NewSpecialisation from '@/Pages/Admin/Specialisations/_New.vue';
import throttle from 'lodash/throttle';
import { createSpecialisationRouteHelper } from '@/utils/createSpecialisationRouteHelper';
import { createLayoutHelper } from '@/utils/layoutHelper';
import ToggleSwitch from '@/Shared/ToggleSwitch.vue';
import SiteContextSelect from '@/Shared/SiteContextSelect.vue';
import RecordUpdateDates from '@/Shared/RecordUpdateDates.vue';
import ContentMissingDot from '@/Shared/ContentMissingDot.vue';
import draggable from 'vuedraggable';
import axios from 'axios';

export default {
  components: {
    Link,
    Icon,
    Pagination,
    Modal,
    NewSpecialisation,
    ToggleSwitch,
    SiteContextSelect,
    RecordUpdateDates,
    ContentMissingDot,
    draggable,
  },
  layout: createLayoutHelper,
  props: {
    specialisations: {
      type: Object,
      required: true,
    },
    // eslint-disable-next-line vue/prop-name-casing
    site_context_options: {
      type: Array,
      default: () => [],
    },
    filters: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      form: {
        name: this.filters.name || '',
        site: this.filters.site || null,
      },
      modalNew: false,
      localSpecialisations: [...this.specialisations.data],
      dragging: false,
      togglingId: null,
      updatingSiteId: null,
    };
  },
  computed: {
    routes() {
      return createSpecialisationRouteHelper(null, this.$routes);
    },
    hasActiveFilters() {
      return !!(this.form.name?.trim() || this.form.site);
    },
    emptyMessage() {
      return this.hasActiveFilters
        ? 'Brak specjalizacji pasujących do filtrów.'
        : 'Nie znaleziono specjalizacji.';
    },
  },
  watch: {
    form: {
      handler: throttle(function () {
        const query = pickBy({
          name: this.form.name?.trim(),
          site: this.form.site || undefined,
        });
        this.$inertia.get(
          this.routes.index(Object.keys(query).length ? query : {}),
          {},
          {
            preserveState: true,
            preserveScroll: true,
            replace: true,
            only: ['specialisations', 'filters'],
          },
        );
      }, 150),
      deep: true,
    },
    '$page.url': {
      handler() {
        if (this.modalNew) {
          this.modalNew = false;
        }
      },
    },
    'specialisations.data': {
      handler(newData) {
        this.localSpecialisations = [...newData];
      },
      deep: true,
    },
  },
  methods: {
    frontofficeHref(specialisation) {
      return this.$routes.specialisation(specialisation.slug);
    },
    resetFilters() {
      this.form.name = '';
      this.form.site = null;
    },
    updateSiteContext(specialisation, siteContext) {
      if (!specialisation.id || this.updatingSiteId) return;
      if ((specialisation.site_context || null) === (siteContext || null)) return;

      this.updatingSiteId = specialisation.id;
      const target = this.localSpecialisations.find((item) => item.id === specialisation.id);
      const previousSiteContext = target?.site_context ?? specialisation.site_context;

      if (target) {
        target.site_context = siteContext;
        target.site_context_label = this.siteLabelFor(siteContext);
        target.missing_site_context = !siteContext;
        target.missing_content = this.missingContentFor(target);
      }

      this.$inertia.put(
        this.routes.update(specialisation.id),
        { specialisation: { site_context: siteContext } },
        {
          preserveScroll: true,
          preserveState: true,
          only: ['specialisations'],
          onError: () => {
            if (target) {
              target.site_context = previousSiteContext;
              target.site_context_label = this.siteLabelFor(previousSiteContext);
              target.missing_site_context = !previousSiteContext;
              target.missing_content = this.missingContentFor(target);
            }
          },
          onFinish: () => {
            this.updatingSiteId = null;
          },
        },
      );
    },
    siteLabelFor(siteContext) {
      if (!siteContext) return '';
      const option = this.site_context_options.find((item) => item.value === siteContext);
      return option?.label || '';
    },
    missingContentFor(specialisation) {
      const labels = [];
      if (!specialisation.site_context) labels.push('Witryna');
      if (specialisation.missing_description) labels.push('Opis');
      return labels;
    },
    editRoute(specialisationId) {
      return specialisationId ? this.routes.edit(specialisationId) : '#';
    },
    toggleActive(specialisation) {
      if (!specialisation.id || this.togglingId) return;
      this.togglingId = specialisation.id;
      const target = this.localSpecialisations.find((item) => item.id === specialisation.id);
      if (target) target.active = !target.active;

      this.$inertia.put(
        this.routes.update(specialisation.id),
        { specialisation: { active: target ? target.active : !specialisation.active } },
        {
          preserveScroll: true,
          preserveState: true,
          only: ['specialisations'],
          onError: () => {
            if (target) target.active = !target.active;
          },
          onFinish: () => {
            this.togglingId = null;
          },
        },
      );
    },
    destroy(specialisation) {
      if (!specialisation.id) return;
      if (confirm('Czy na pewno chcesz usunąć tę specjalizację?')) {
        this.$inertia.delete(this.routes.destroy(specialisation.id), {
          preserveScroll: true,
        });
      }
    },
    async onDragEnd() {
      this.dragging = false;

      const ids = this.localSpecialisations.map(specialisation => specialisation.id);

      try {
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

        await axios.post(this.routes.reorder(), {
          ids: ids,
        }, {
          headers: {
            'X-CSRF-Token': csrfToken,
          },
        });

        this.$inertia.reload({ only: ['specialisations'] });
      } catch (error) {
        console.error('Failed to reorder specialisations:', error);
        this.localSpecialisations = [...this.specialisations.data];
        alert('Nie udało się zmienić kolejności specjalizacji. Spróbuj ponownie.');
      }
    },
    onDragStart() {
      this.dragging = true;
    },
  },
};
</script>
